import React, { useState } from 'react';
import moment from 'moment';
import R from 'ramda';
import PropTypes from 'prop-types';

import { Dimmer, Loader, Menu, Button } from 'semantic-ui-react';

import { AC_TYPES, LICENSE_POOL_TYPES } from '../../../server/constants';
import { sixMonthsInMinutes } from '_constants'
import { getMaxExpiryDateForPool, getMaxExpiresDaysAfterInitForPool } from '_utils/licensePools';
import { post } from '_utils/xhr';

import CreateModalWrapper from '_forms/createModal';
import ACCreateForm from '_forms/acBatchInstitution';
import CreatedACLicensesList from './createdACList';

const getInitData = (institution) => ({
  license_pool_id: null,
  type: null,
  count: 1,
  license_count: 1,
  session_length_min: sixMonthsInMinutes,
  expires_on: null,
  expires_days_after_init: null,
  description: ''
});

const PHASES = {
  FORM: 0,
  SUBMITTING: 1,
  COMPLETE: 2
};

const CreateInstitutionACLicensesModal = ({
  institution,
  onRefreshInstitution,
  onRefreshLicenses
}) => {

  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ phase: PHASES.FORM, error: null, codes: null });
  const [data, setData] = useState(getInitData(institution));

  const onOpen = () => setOpen(true);

  const onReset = (e) => {
    e.preventDefault();
    onRefreshInstitution();
    setData(getInitData(institution));
    setState({ phase: PHASES.FORM, error: null, codes: null });
  };

  const onClose = (e) => {
    onReset(e);
    setOpen(false);
  };

  const onFormChange = (name, value) => {

    const up = Object.assign({}, data, { [name]: value });

    const pool = institution.license_pools.find(p => p._id === up.license_pool_id);
    if (!pool) return;

    if (up.expires_on) up.expires_on = moment(up.expires_on).utc().endOf('day').toDate();

    // Adjust data to new license pool details
    if (name === 'license_pool_id') {

      if (pool.type === LICENSE_POOL_TYPES.FULL) {
        up.type = AC_TYPES.FULL;
        up.expires_on = null;
        up.expires_days_after_init = null;
      } else if (pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION) {
        up.type = AC_TYPES.TERM_DATE;
        up.expires_on = pool.expires_on;
        up.expires_days_after_init = null;
      }
    }

    const oneDay = moment().add(1, 'days');

    // Reset Expiry upon type change
    if (name === "type") {
      switch (value) {
        case AC_TYPES.FULL: {
          up.expires_on = null;
          up.expires_days_after_init = null;
          break;
        }
        case AC_TYPES.TERM_DATE: {
          up.expires_days_after_init = null;
          up.expires_on = oneDay;
          break;
        }
        case AC_TYPES.TERM_DAYS:
        default: {
          up.expires_on = null;
          up.expires_days_after_init = 30;
          break;
        }
      }
    }

    // Clamp Term ACs for a SUBSCRIPTION Pool

    if (pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION && up.expires_on) {
      up.expires_on = new Date(
        R.clamp(
          oneDay.valueOf(),
          moment(getMaxExpiryDateForPool(pool)).valueOf(),
          moment(up.expires_on).valueOf()
        )
      );
    } else if (pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION && up.expires_days_after_init) {
      up.expires_days_after_init = R.clamp(1, getMaxExpiresDaysAfterInitForPool(pool), up.expires_days_after_init);
    }

    // Adjust and clamp license counts
    const maxCount = (pool.ac_count - pool.usage.activation_codes.active.total) || 1;
    up.license_count = R.clamp(1, maxCount, up.license_count);
    up.count = R.clamp(1, maxCount / up.license_count, up.count);

    setData(up);
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    setState({ phase: PHASES.SUBMITTING, error: null, codes: null });

    const payload = Object.assign({}, data);

    // ISO Date if present
    if (payload.expires_on) {
      payload.expires_on = payload.expires_on.toISOString();
    }

    post(
      `/institutions/${encodeURIComponent(institution._id)}/activationcodes`,
      payload
    )
      .then(codes => {
        // if (this.props.createBatch) {
        //   this.props.createBatch(body)
        // }
        setState({ phase: PHASES.COMPLETE, error: null, codes });
        onRefreshLicenses();
      })
      .catch(err => setState({ phase: PHASES.FORM, error: err, codes: null }));
  };

  return (
    <CreateModalWrapper
      onClose={ onClose }
      onOpen={ onOpen }
      onDismiss={ onClose }
      open={ open }
      header='Create New Institution Activation Codes'
      subHeader={ institution.name }
      type='activation codes'
      trigger={
        <Menu.Item name='add_institution_acs'>
          <Button icon='ticket' content='Create Codes' />
        </Menu.Item>
      }
    >
      <div>
        <Dimmer inverted active={ state.phase === PHASES.SUBMITTING } >
          <Loader />
        </Dimmer>
        {/* Show potential error */}

        {
          state.phase === PHASES.FORM ? (
            <ACCreateForm
              data={ data }
              institution={ institution }
              onChange={ onFormChange }
              onSubmit={ onFormSubmit }
            />
          ) : null
        }
        {
          state.phase === PHASES.COMPLETE ? (
            <CreatedACLicensesList onReset={ onReset } codes={ state.codes || [] } />
          ) : null
        }
      </div>
    </CreateModalWrapper>
  );
};

CreateInstitutionACLicensesModal.propTypes = {
  institution: PropTypes.object.isRequired,
  onRefreshInstitution: PropTypes.func.isRequired,
  onRefreshLicenses: PropTypes.func.isRequired
};

export default CreateInstitutionACLicensesModal;



// WEBPACK FOOTER //
// ./webAdmin/views/activationCodes/createBatchInstAcModal.js